import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { AuthProvider } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import type { FetchArgs } from '@reduxjs/toolkit/query';
import { store } from '@/redux/store';
import { userManager, OIDCState } from '@/auth/oidcConfig';
import '@/lib/DatadogService';
import {
  userLoaded,
  userUnloaded,
  userLazyClaims,
  userClaimsFetched
} from '@/redux/slices/authSlice';
import App from './App';
import ToastContainer from '@/components/ui/Toast/ToastContainer';
import '@/styles/index.css';
import { contentDeliveryApi } from './redux/api/contentDeliveryAPI';
import { ClientAPI, repeatAction } from './redux/api';
import {
  buildPostSignInAction,
  buildParams,
  buildDelayedActionUrls
} from './redux/helpers/delayedUserActions';
import { registerSW } from 'virtual:pwa-register';
import { overrideAuth } from './auth/mobile-webview-auth';
import { trackAccountLoginEvent } from './analytics/user';
import { checkConsent } from './utils/clarityConsent';
import { initTrustArcCustomCSS } from './trustarcCustomCSS';
import { runAfterCoreContent } from '@/utils/coreContentReady';

const invalidateUserData = () => {
  contentDeliveryApi.util.invalidateTags(['Global', 'Content']);
  ClientAPI.util.invalidateTags([
    'CartData',
    'CreditTrackerData',
    'LibraryItem',
    'Library',
    'POECRenewalItems'
  ]);
};

const onSigninCallback = async (user: void | User) => {
  console.info('DIAG:Excecute post sign in action');
  const { isPm, isIm } = await fetchLazyClaims(user);
  store.dispatch(
    userLoaded({
      token: user?.access_token,
      name: user?.profile.name,
      individualSk: user?.profile.IndividualSK as string
    })
  );
  console.info('DIAG: (main) User loaded: ' + user?.profile.name + ' ' + user?.access_token);
  const state: OIDCState = user?.state as OIDCState;
  if (typeof state?.scrollToElementId === 'string') {
    try {
      localStorage.setItem('postSignInScrollElementId', state.scrollToElementId);
    } catch {
      /* empty */
    }
  }
  const returnUrl: string = state?.returnUrl ? state.returnUrl : '/';
  invalidateUserData();
  const returnUrlObj = new URL(returnUrl, window.location.origin);

  const [postSignInAction] = await Promise.all([
    buildPostSignInAction(state, isPm),
    user && trackAccountLoginEvent(isPm, isIm, user.profile)
  ]);

  const returnUrlHasSearchParams = returnUrlObj.searchParams.size > 0;

  let returnUrlWithSuccessToast = `${returnUrl}${returnUrlHasSearchParams ? '&' : '?'}${buildDelayedActionUrls(
    'success',
    postSignInAction?.endpoint || 'noAction',
    postSignInAction.message
  )}`;
  const returnUrlWithErrorToast = `${returnUrl}${returnUrlHasSearchParams ? '&' : '?'}${buildDelayedActionUrls('error', postSignInAction?.endpoint || 'noAction')}`;

  if (
    state.endpoint &&
    state.arg &&
    postSignInAction?.endpoint &&
    !state?.extraOptions?.ignoreDelayedAction
  ) {
    const { endpoint, body, url, param } = postSignInAction;

    if (state.arg && endpoint && (body || param)) {
      state.arg =
        typeof state.arg === 'object'
          ? ({ ...state.arg, body, url, param } as FetchArgs)
          : state.arg;
      repeatAction(state.arg, endpoint, store, state.extraOptions)
        .then(response => {
          if (response && response.data) {
            returnUrlWithSuccessToast += `&${buildParams({ response: response.data })}`;
          }
          window.history.replaceState({ response }, document.title, returnUrlWithSuccessToast);
          console.info('DIAG:Reloaded by main, repeat action');
          window.location.reload();
        })
        .catch(error => {
          console.error(error);
          window.history.replaceState({ error }, document.title, returnUrlWithErrorToast);
          console.info('DIAG:Reloaded by main, repeat action error');
          window.location.reload();
        });
    } else {
      window.history.replaceState({}, document.title, returnUrl);
      console.info('DIAG:Reloaded by main, 1');
      window.location.reload();
    }
  } else if (postSignInAction.endpoint === null && state.endpoint) {
    window.history.replaceState({}, document.title, returnUrlWithSuccessToast);
    console.info('DIAG:Reloaded by main, 2');
    window.location.reload();
  } else {
    window.history.replaceState({}, document.title, returnUrl);
    console.info('DIAG:Reloaded by main, 3');
    window.location.reload();
  }
};
const onRemoveUser = () => {
  store.dispatch(userUnloaded());
  userManager.clearStaleState();
  invalidateUserData();
  console.info('DIAG:Remove user. ');
  //window.history.replaceState({}, document.title, window.location.pathname);
};

const serviceWorkerUpdateIntervalMS = 5 * 60 * 1000;
const serviceWorkerUpdateThrottleMS = 60 * 1000;
let lastServiceWorkerUpdateCheck = 0;

const updateServiceWorkerRegistration = async (
  registration: ServiceWorkerRegistration,
  force = false
) => {
  if (registration.installing || !navigator.onLine) return;

  const now = Date.now();
  if (!force && now - lastServiceWorkerUpdateCheck < serviceWorkerUpdateThrottleMS) return;
  lastServiceWorkerUpdateCheck = now;

  try {
    await registration.update();
  } catch (error) {
    console.warn('DIAG: Failed to check service worker update.', error);
  }
};

const registerServiceWorkerUpdateChecks = (registration: ServiceWorkerRegistration) => {
  const update = (force = false) => updateServiceWorkerRegistration(registration, force);

  update(true);
  setInterval(update, serviceWorkerUpdateIntervalMS);

  window.addEventListener('pageshow', () => update());
  window.addEventListener('focus', () => update());
  window.addEventListener('online', () => update(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') update();
  });
};

registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, r) {
    if (!r) return;

    registerServiceWorkerUpdateChecks(r);
  },
  onRegisterError(error) {
    console.warn('DIAG: Service worker registration failed.', error);
  }
});

async function fetchLazyClaims(user: void | User) {
  let isPm = false;
  let isIm = false;
  let userClaimsExtended = undefined;

  if (user?.access_token && !store.getState()?.auth?.claimFetched) {
    try {
      console.info('DIAG: (Main) Fetching LazyClaims Api');

      const resp = await fetch(
        `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/user/contract/statuses`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${user.access_token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      if (resp.ok) {
        userClaimsExtended = await resp.json();

        isPm = userClaimsExtended?.isPM ?? false;
        isIm = userClaimsExtended?.isIM ?? false;

        store.dispatch(
          userLazyClaims({
            isIm: isIm,
            isPm: isPm
          })
        );
        store.dispatch(userClaimsFetched(true));
        console.info('DIAG: (main) User Claims loaded: ' + 'isPm:' + isPm + ' isIm:' + isIm);
      } else {
        console.error(
          'DIAG: (main) Failed to fetch (Main) LazyClaims Api response is not ok',
          resp.statusText
        );
      }
    } catch (e) {
      console.error('DIAG: (main) Failed to fetch (Main) LazyClaims Api', e);
    }
  }
  return { isPm, isIm };
}

runAfterCoreContent(checkConsent);
initTrustArcCustomCSS();

// Expose userManager for testing in dev mode
//
// Example usage in browser console (copy/paste):
/*
//Check current token expiration
const user = await window.oidcUserManager?.getUser();
console.log('Expires at:', new Date(user.expires_at * 1000));
console.log('Time remaining:', Math.floor((user.expires_at * 1000 - Date.now()) / 60000), 'minutes');

// Force token to expire by manipulating the expiry time
user.expires_at = Math.floor(Date.now() / 1000) - 1;
await window.oidcUserManager.storeUser(user);
console.log('Token marked as expired - silent renewal should trigger soon');
*/
if (
  import.meta.env.MODE?.toLowerCase() === 'development' ||
  import.meta.env.VITE_APP_ENVIRONMENT_NAME?.toLowerCase() === 'test'
) {
  (window as Window & { oidcUserManager?: typeof userManager }).oidcUserManager = userManager;
}

// Add event listeners to monitor token renewal
// These are registered once at module load time, before render
userManager.events.addAccessTokenExpiring(() => {
  console.info('DIAG: Access token is expiring soon - silent renewal will be attempted');
});

userManager.events.addAccessTokenExpired(() => {
  console.warn('DIAG: Access token has expired!');
});

userManager.events.addSilentRenewError(error => {
  console.error('DIAG: Silent token renewal failed:', error);
});

userManager.events.addUserLoaded(user => {
  console.info('DIAG: User loaded/refreshed. Token expires at:', new Date(user.expires_at! * 1000));
});

async function render() {
  if (window.overrideAuthentication) await overrideAuth(window.overrideAuthentication);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <ReduxProvider store={store}>
      <AuthProvider
        userManager={userManager}
        onSigninCallback={onSigninCallback}
        onRemoveUser={onRemoveUser}
      >
        <ToastContainer />
        <App />
      </AuthProvider>
    </ReduxProvider>
  );

  (window as unknown as Record<string, string>).assureLoading = import.meta.env.VITE_BUILD_NUMBER;
}

render();
